import { makeStyles } from "@src/utils/genericComponentProps";
import { t } from "i18next";
import { useCallback, useMemo } from "react";
import Skeleton from "react-loading-skeleton";
import Select, { ActionMeta, OnChangeValue } from "react-select";
import TypographyText from "../../TypographyText/TypographyText";
import {
  SelectInput_CustomClearIndicator,
  SelectInput_CustomControl,
  SelectInput_CustomDropdownIndicator,
  SelectInput_CustomMenu,
  SelectInput_CustomMultiValueRemove,
  SelectInput_CustomOption,
  SelectInput_CustomValueContainer,
} from "./CustomSelectInputComponents";
import { getSelectInputStyling } from "./SelectInput.styling";
import { SelectInputOption, SelectInputProps } from "./SelectInput.types";

const getSortedOptions = (options: SelectInputOption[], useCustomSort?: boolean): SelectInputOption[] => {
  return useCustomSort ? options : [...options].sort((a, b) => a.label.localeCompare(b.label, "en", { numeric: true }));
};

/**
 * The SelectInput component renders a customizable select input using the react-select library.
 * It supports both single and multi-select modes, various customizations, and additional features.
 *
 * @template IsMulti - Determines if the select allows multiple selections.
 * @template TOptions - Array of SelectInputOption objects representing the selectable options.
 * @param props - Props for the SelectInput component.
 * @returns The SelectInput component.
 */
export const SelectInput = <IsMulti extends boolean, TOptions extends SelectInputOption[]>(props: SelectInputProps<IsMulti, TOptions>) => {
  const selectStyling = getSelectInputStyling(props);
  const sortedOptions = useMemo(() => getSortedOptions(props.options, props.useCustomSort), [props.options, props.useCustomSort]);

  const handleChange = useCallback(
    (newValue: OnChangeValue<SelectInputOption, IsMulti>, actionMeta: ActionMeta<SelectInputOption>) => {
      if (props.isMulti) {
        let selected: readonly string[] = [];

        if (Array.isArray(newValue)) {
          selected = newValue.map((option) => option.value);
        }

        switch (actionMeta.action) {
          case "remove-value":
          case "pop-value":
            if (actionMeta.removedValue?.fixed) {
              return;
            }
            break;
          case "clear":
            selected = sortedOptions.filter((v) => v.fixed).map((v) => v.value) as readonly string[];
            break;
        }

        props.onChange(selected as IsMulti extends true ? readonly string[] : string | null);
      } else {
        const selected = newValue ? (newValue as TOptions[number]).value : null;
        props.onChange(selected as IsMulti extends true ? readonly string[] : string | null);
      }
    },
    [props.isMulti, props.onChange, sortedOptions]
  );

  return (
    <div data-cy={props.dataCy} className={props.className} style={{ ...makeStyles(props), ...props.style }}>
      {props.label && (
        <TypographyText size="sm" weight="medium" color="var(--gray-700)" marginBottom={6}>
          {!props.isLoading && props.label}
          {props.isLoading && <Skeleton width={props.width ?? "100%"} height="undefined" />}
        </TypographyText>
      )}
      {props.isLoading && <Skeleton width={props.width ?? "100%"} height={props.size === "xs" ? "36px" : props.size ? "40px" : "44px"} />}
      {!props.isLoading && (
        <Select
          ref={props.inputRef}
          isDisabled={props.isDisabled}
          isLoading={props.isFetching && !props.isLoading}
          hideSelectedOptions={false}
          styles={selectStyling}
          placeholder={props.placeholder ?? t("main.select")}
          closeMenuOnSelect={!props.isMulti}
          classNamePrefix="react-select"
          isMulti={props.isMulti}
          value={
            props.isMulti
              ? props.options.filter((option) => (props.value as readonly string[]).includes(option.value))
              : props.options.find((option) => option.value === props.value)
          }
          options={sortedOptions}
          menuPortalTarget={document.body}
          defaultMenuIsOpen={props.defaultMenuIsOpen}
          onChange={handleChange}
          isClearable={props.isClearable}
          isSearchable={props.isSearchable ?? true}
          isOptionDisabled={(option) => (option.disabled || option.fixed) ?? false}
          components={{
            Control: SelectInput_CustomControl,
            ValueContainer: SelectInput_CustomValueContainer,
            MultiValueRemove: SelectInput_CustomMultiValueRemove,
            Option: SelectInput_CustomOption,
            ClearIndicator: SelectInput_CustomClearIndicator,
            DropdownIndicator: SelectInput_CustomDropdownIndicator,
            Menu: SelectInput_CustomMenu,
          }}
        />
      )}
      {props.helperText && (
        <TypographyText size="sm" color="var(--gray-600)" marginTop={6} truncate={false}>
          {!props.isLoading && props.helperText}
          {props.isLoading && <Skeleton width={props.width ?? "100%"} />}
        </TypographyText>
      )}
    </div>
  );
};
